import { useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { selectUser } from './redux/slices/userSlice';
import { ToasterProvider } from './components/Toaster';
import LoadingOverlayFM from './components/LoadingOverlayFM';

import Home from "./pages/Home";
import TeamSetting from "./pages/TeamSetting";
import TeamCreate from "./pages/TeamCreate";
import AssistantCreate from "./pages/AssistantCreate";
import FileSetting from "./pages/FileSetting";
import Navbar from "./components/Navbar";
import OAuthCallback from './pages/OAuthCallback';
import Login from "./pages/Login";
import PublicChat from "./pages/PublicChat";

// ✅ 你有聊天頁就打開這行
// import PublicChat from "./pages/PublicChat";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function RequireAuth() {
  const { status, loading } = useSelector(selectUser);
  const location = useLocation();

  if (loading) return null;

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

function RedirectIfAuth() {
  const { status } = useSelector(selectUser);
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<RedirectIfAuth />}>
        <Route path="/login" element={<Login />} />
        <Route path="/oauth2/callback" element={<OAuthCallback />} />
      </Route>

      <Route path="/chat/:assistantToken" element={<PublicChat />} />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<Home />} />
        <Route path="/team-setting" element={<TeamSetting />} />
        <Route path="/team-create" element={<TeamCreate />} />
        <Route path="/assistant-create" element={<AssistantCreate />} />
        <Route path="/file-setting" element={<FileSetting />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function AppLayout() {
  const { pathname } = useLocation();
  const isChat = pathname.startsWith("/chat/");

  const writeNavHeight = useCallback(() => {
    const nav = document.getElementById('appNavbar');
    const h = nav ? nav.getBoundingClientRect().height : 0; // ✅ 沒 navbar 就是 0
    document.documentElement.style.setProperty('--nav-height', `${h}px`);

    // ✅ 搭配 index.css 的 --page-top-gap（你有做第 1 步才會有效）
    document.documentElement.style.setProperty('--page-top-gap', isChat ? '0px' : '8px');
  }, [isChat]);

  useEffect(() => {
    writeNavHeight();
  }, [pathname, writeNavHeight]);

  useEffect(() => {
    writeNavHeight();

    // 聊天頁沒有 Navbar，不需要監聽全頁每一個 transitionend。
    // 原本任何 Bootstrap/button/toast transition 結束都會進來寫 CSS variables，
    // 對互動頻繁的聊天頁是多餘的 style invalidation。
    if (isChat) return undefined;

    window.addEventListener('resize', writeNavHeight);
    document.addEventListener('transitionend', writeNavHeight, true);

    return () => {
      window.removeEventListener('resize', writeNavHeight);
      document.removeEventListener('transitionend', writeNavHeight, true);
    };
  }, [isChat, writeNavHeight]);

  useEffect(() => {
    document.body.classList.toggle('public-chat-performance', isChat);
    return () => document.body.classList.remove('public-chat-performance');
  }, [isChat]);

  return (
    <>
      <LoadingOverlayFM />

      {/* ✅ 聊天頁不要 Navbar */}
      {!isChat && <Navbar />}

      {/* ✅ 聊天頁不要 container / mt-2 / mb-5，直接滿版 */}
      {isChat ? (
        <div className="p-0 m-0">
          <AppRoutes />
        </div>
      ) : (
        <div className="container mt-2 mt-md-4 mb-3 mb-md-5">
          <AppRoutes />
        </div>
      )}
    </>
  );
}

export default function App() {
  if (!googleClientId) {
    console.error('REACT_APP_GOOGLE_CLIENT_ID 沒有設定');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ToasterProvider>
        <BrowserRouter>
          <AppLayout />
        </BrowserRouter>
      </ToasterProvider>
    </GoogleOAuthProvider>
  );
}
